/**
 * Shared layout constants for in-game UI components.
 *
 * The game uses absolute positioning rather than a CSS grid, so a few
 * components depend on each other's offsets: `game-log` clamps its scroll
 * area to stay above `.sidebar`, which airmash-game places from the same
 * number, menu-drawer hides the log's switch when the log cannot fit, and
 * airmash-logo swaps its mark at the same width in CSS and in its
 * `<picture>`. Change a number here and every consumer follows.
 */

import { NOTICE_COLUMN_MAX_PX } from '../../theme/notice.css';

/** Vertical offset of `.sidebar` from the viewport vertical center, in px. */
export const SIDEBAR_OFFSET_FROM_CENTER = 290;

/**
 * The first line below the top bar's chips, in px from the viewport top; each
 * consumer adds the status-bar inset itself. The `game-log` host starts here,
 * and `.sidebar` comes no higher: at `50% - 290px` it ran into the logo on
 * any viewport under 690px tall and off the top under 580px, as legacy's did.
 */
export const TOP_BAR_BOTTOM = 55;

/** Distance from viewport top to the top of the `game-log` host, in px. */
export const GAME_LOG_TOP = TOP_BAR_BOTTOM;

/** Minimum gap between game-log bottom and the sidebar top, in px. */
export const GAME_LOG_BOTTOM_GAP = 10;

/** Floor for the game-log scroll area height, in px. */
export const GAME_LOG_MIN_HEIGHT = 80;

/** Viewport width (px) below which the airmash-logo switches its word mark
 *  for the square mark. Width-only — large landscape phones still have room
 *  for the word mark horizontally, so the threshold is a width threshold. */
export const MOBILE_BREAKPOINT_PX = 768;

/**
 * The floor the interface is made for, under which the layout is best effort
 * and the player is told once per game (UI.ts's noticeViewportFloor). On a
 * desktop the sidebar runs from TOP_BAR_BOTTOM to 523px, so a shorter window
 * cuts the selectors off; the panels fit from 500px, the chat box shares the
 * left edge with the sidebar's bottom under about 700px, as legacy's did, and
 * the game log hides itself under 870px. On a phone the touch cluster is
 * scaled down at 360px of height (touch-controls) and the top bar overflows
 * under 360px of width.
 */
export const MIN_DESKTOP_HEIGHT = 540;
export const MIN_MOBILE_SHORT_SIDE = 360;

/** The notice for a viewport under the floor, or null when it fits. */
export function viewportFloorMessage(viewport: { width: number; height: number }, isMobile: boolean): string | null {
  if (isMobile) {
    return Math.min(viewport.width, viewport.height) < MIN_MOBILE_SHORT_SIDE
      ? `This screen is smaller than the game is made for\nUnder ${MIN_MOBILE_SHORT_SIDE} pixels across, the controls and notices crowd it`
      : null;
  }
  return viewport.height < MIN_DESKTOP_HEIGHT
    ? `This window is shorter than the game is made for\nUnder ${MIN_DESKTOP_HEIGHT} pixels tall, the selectors on the left are cut off`
    : null;
}

/**
 * Vertical pixels available between the bottom of the game-log host
 * (`GAME_LOG_TOP + GAME_LOG_BOTTOM_GAP`) and the top of `.sidebar`
 * (`viewport.height / 2 - SIDEBAR_OFFSET_FROM_CENTER`). Drives the log's
 * scroll-area max-height and the fit check.
 */
export function gameLogAvailableHeight(viewport: { height: number }): number {
  return viewport.height / 2 - SIDEBAR_OFFSET_FROM_CENTER - GAME_LOG_TOP - GAME_LOG_BOTTOM_GAP;
}

/**
 * True when `game-log` can show: not on mobile, and enough vertical room to
 * fit above `.sidebar` without overlap. Mobile has no sidebar to measure
 * against, and its top band belongs to the chat button, the game-mode status
 * and the status notices. Used by both `game-log` itself and the matching
 * setting in `menu-drawer` so the toggle disappears exactly when the log
 * can't show.
 */
export function gameLogFits(ui: { isMobile: boolean; viewport: { height: number } }): boolean {
  return !ui.isMobile && gameLogAvailableHeight(ui.viewport) >= GAME_LOG_MIN_HEIGHT;
}

/**
 * True when the transient notices stack in airmash-game's column (a phone, a
 * short window): the TS side of the notices' `(max-width), (max-height)`
 * rules, for a notice that gives way to another there.
 */
export function noticesStack(viewport: { width: number; height: number }): boolean {
  return viewport.width <= NOTICE_COLUMN_MAX_PX || viewport.height <= NOTICE_COLUMN_MAX_PX;
}
